import { z } from "zod";

export type OverrideProperty = { key: string; template: string };

const zProperties = z.record(z.string(), z.string());

const zStoredOverrideEntry = z.object({
  // Origin composer ids this entry is scoped to; omitted => all targets.
  composers: z.array(z.string()).optional(),
  override: z.object({
    entity: z.object({ properties: zProperties }).optional(),
    relation: z.object({ properties: zProperties }).optional(),
  }),
});
export type StoredOverrideEntry = z.infer<typeof zStoredOverrideEntry>;

const zDirectiveScope = z.object({
  composers: z.array(z.string()).optional(),
  categories: z.array(z.string()).optional(),
});

const zStoredKeyRename = zDirectiveScope.extend({
  from: z.string(),
  to: z.string(),
});
export type StoredKeyRename = z.infer<typeof zStoredKeyRename>;

const zStoredRetype = zDirectiveScope.extend({
  key: z.string(),
  fromType: z.string(),
  toType: z.string(),
  fromAttributeType: z.string().nullable().optional(),
  toAttributeType: z.string().nullable().optional(),
});
export type StoredRetype = z.infer<typeof zStoredRetype>;

// The record_update composer definition JSON. Additional keys (lifecycle,
// targetSetHash) are preserved by the backend and irrelevant to the editor.
export const zRecordUpdateComposerDefinition = z
  .object({
    lifecycle: z.string().optional(),
    targetSetHash: z.string().optional(),
    overrides: z.array(zStoredOverrideEntry).optional(),
    keyRenames: z.array(zStoredKeyRename).optional(),
    retypes: z.array(zStoredRetype).optional(),
  })
  .passthrough();

// Mutation input entry shape (list-of-pairs, not a Record).
export type OverrideInputEntry = {
  composers?: string[];
  entityProperties?: OverrideProperty[];
  relationProperties?: OverrideProperty[];
};

// The editor's in-memory model. `editable` is false when the stored overrides use
// a shape the simple all-targets editor cannot round-trip (scoped entries, or more
// than one unscoped entry); in that case the panel shows them read-only.
export type OverrideFormModel = {
  editable: boolean;
  entity: OverrideProperty[];
  relation: OverrideProperty[];
  preservedScoped: StoredOverrideEntry[];
  blockedReason: null | "scoped" | "multipleUnscoped";
};
